import { Breadcrumb, Card, Modal, Table } from "antd";
import { useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { getUserFromStorage } from "../../util/localStorage.util";
import CoverWizardModal, {
  CoverWizardModalPayload,
} from "./OrderContextMenu/CoverWizardModal/CoverWizardModal";
import { PaymentModal } from "./PaymentModal/PaymentModal";
import useGetFeatureFlagStatuses from "../../hooks/useGetFeatureFlagStatuses";
import OrderDto from "../../store/actions/dtos/OrderDto";
import { EditOrderAttachmentsModal } from "./EditOrderAttachementsModal/EditOrderAttachmentsModal";
import {
  SubmittedOrderModal,
  SubmittedOrderModalPayload,
} from "./OrderContextMenu/CoverWizardModal/components/SubmittedOrderModal";
import { OrdersSearchCard } from "./OrderSearchCard/OrdersSearchCard";
import { useOrderList } from "./hooks/useOrderList";
import { useFileModal } from "./hooks/useFileModal";
import { usePaymentFlow } from "./hooks/usePaymentFlow";
import { useGroupFilters } from "./hooks/useGroupFilters";
import { buildOrderColumns, buildFileColumns } from "./OrderTable/orderColumns";
import { OrderDetailPanel } from "./OrderDetailPanel/OrderDetailPanel";

const storageInfo = getUserFromStorage();
const isAuthenticated = !!storageInfo;
const isAdmin = isAuthenticated && JSON.parse(storageInfo!).isAdmin;

export type { SelectedOrder } from "./OrderPage.types";

export const OrderPage = () => {
  const history = useHistory();
  const orderList = useOrderList();
  const groupFilters = useGroupFilters(
    orderList.getOrderList,
    orderList.listOrdersPayload,
    orderList.setListOrdersPayload
  );
  const payment = usePaymentFlow(() =>
    orderList.getOrderList({
      ...orderList.listOrdersPayload,
      SearchByGroups: groupFilters.selectedGroupsFilters.join(","),
    })
  );
  const fileModal = useFileModal();

  const {
    newOrderButton,
    newRgaButton,
    newHardwareOrderButton,
    cloneOrderButton,
    replacementLookup,
  } = useGetFeatureFlagStatuses();

  const [poolToolWizardPayload, setPoolToolWizardPayload] =
    useState<CoverWizardModalPayload | null>(null);
  const [submittedModalPayload, setSubmittedModalPayload] =
    useState<SubmittedOrderModalPayload | null>(null);
  const [editAttachmentsModalPayload, setEditAttachmentsModalPayload] =
    useState<OrderDto | null>(null);

  const reloadOrders = () =>
    orderList.getOrderList({
      ...orderList.listOrdersPayload,
      SearchByGroups: groupFilters.selectedGroupsFilters.join(","),
    });

  const columns = buildOrderColumns({
    fileLoading: fileModal.fileLoading,
    fileSelectedSoNo: fileModal.selectedOrder?.soNo,
    onOpenFileModal: fileModal.openFileModal,
    paymentLoading: false,
    paymentSelectedSoNo: payment.modalPayload?.soNo,
    onShowPayment: payment.showPayment,
    isAuthenticated,
    isAdmin,
    cloneOrderButton,
    coverWizardModalPayloadState: [poolToolWizardPayload, setPoolToolWizardPayload],
    editAttachmentsModalPayloadState: [editAttachmentsModalPayload, setEditAttachmentsModalPayload],
    onReloadOrders: reloadOrders,
  });

  return (
    <>
      <div>
        <div className="header__breadcrumb">
          <h1>Orders</h1>
          <Breadcrumb>
            <Breadcrumb.Item>Home</Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/orders">Orders</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <OrdersSearchCard
          newOrderButton={newOrderButton}
          newRgaButton={newRgaButton}
          cloneOrderButton={cloneOrderButton}
          newHardwareOrderButton={newHardwareOrderButton}
          replacementLookup={replacementLookup}
          setPoolToolWizardPayload={setPoolToolWizardPayload}
          onSearch={(values) => {
            const payload = {
              ...orderList.listOrdersPayload,
              ...values,
              SearchByGroups: groupFilters.selectedGroupsFilters.join(","),
            };
            orderList.setListOrdersPayload(payload);
            orderList.getOrderList(payload);
          }}
          submittedModalPayloadState={[submittedModalPayload, setSubmittedModalPayload]}
          groupFilters={groupFilters.groupFilters}
          selectedGroupsFilters={groupFilters.selectedGroupsFilters}
          onGroupFilterChange={groupFilters.handleGroupFilterChange}
          onViewAll={groupFilters.handleViewAll}
          onReplacementLookup={() => history.push("/replacement-lookup")}
        />
        <Card>
          <h3>Order List</h3>
          <div className="table-responsive">
            <Table
              bordered
              loading={orderList.loading}
              pagination={{
                defaultPageSize: 20,
                total: orderList.totalPage,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
              }}
              columns={columns}
              dataSource={orderList.tableData}
              onChange={orderList.handleTableChange}
              expandable={{
                expandedRowRender: (record) => (
                  <OrderDetailPanel
                    record={record}
                    onPayNow={() => payment.showPayment(record.customer || record.file)}
                    onOrderUpdated={reloadOrders}
                  />
                ),
                rowExpandable: () => true,
                expandRowByClick: true,
              }}
            />
          </div>
        </Card>
        <Modal
          title={`List of files for "${fileModal.selectedOrder?.soNo}"`}
          visible={fileModal.fileModal}
          footer={null}
          onCancel={fileModal.closeFileModal}
          width={900}
          style={{ paddingBottom: 0 }}
        >
          <Table
            columns={buildFileColumns(
              fileModal.fileLoading,
              fileModal.downloadLoading,
              fileModal.viewFile,
              fileModal.downloadFile
            )}
            dataSource={fileModal.fileList}
            pagination={false}
          />
        </Modal>
      </div>
      {!!payment.modalPayload && (
        <PaymentModal
          modalPayloadState={[payment.modalPayload, payment.setModalPayload]}
          onPaymentSuccess={payment.handlePaymentSuccess}
        />
      )}
      {!!poolToolWizardPayload && (
        <CoverWizardModal
          modalPayloadState={[poolToolWizardPayload, setPoolToolWizardPayload]}
          submittedModalPayloadState={[submittedModalPayload, setSubmittedModalPayload]}
          onSubmit={reloadOrders}
        />
      )}
      {!!editAttachmentsModalPayload && (
        <EditOrderAttachmentsModal
          modalPayloadState={[editAttachmentsModalPayload, setEditAttachmentsModalPayload]}
          onSubmit={reloadOrders}
        />
      )}
      {!!submittedModalPayload && (
        <SubmittedOrderModal
          submittedModalPayloadState={[submittedModalPayload, setSubmittedModalPayload]}
        />
      )}
    </>
  );
};
